import React, { useEffect, useState } from 'react'
import style from './Styles/index.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import { FormattedMessage } from 'react-intl'
import { actualSchemaVersion } from '../../versions'
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark'
import { checkVersion, checkVersionsMatch } from '../../versions/utils'
import { selectProductsConfigs } from '../../store/selectors/configSelectors'
import { useAppSelector } from '../../hooks/useAppSelector'
import { useAppDispatch } from '../../hooks/useAppDispatch'
import { User } from '../../entity/User'
import { removeDismissedTimestamp, setDismissedTimestamp, shouldShowNotification } from './helpers'

interface Props {
    guid: string
}

export const ConfigVersionMismatchPopup = ({ guid }: Props) => {
    const dispatch = useAppDispatch()
    const user = new User()
    const productsConfigs = useAppSelector(selectProductsConfigs)
    const productConfig = productsConfigs?.[guid]
    const schemaVersion = productConfig?.schema_version

    const isVersionValid = checkVersion(schemaVersion)
    const isSchemasVersionsMatch = checkVersionsMatch(
        schemaVersion,
        actualSchemaVersion
    )

    const [showVersionMismatch, setShowVersionMismatch] = useState(false)

    const handleClose = () => {
        setShowVersionMismatch(false)
        if (user.login) {
            setDismissedTimestamp(guid, user.login)
        }
    }

    useEffect(() => {
        if (!productConfig || !user.login) return

        const hasMajorMismatch = !isSchemasVersionsMatch.major || !isVersionValid
        const hasMinorMismatch = !isSchemasVersionsMatch.minor && isVersionValid
        const hasAnyMismatch = hasMajorMismatch || hasMinorMismatch
        const versionsFullyCompatible = isSchemasVersionsMatch.major && isSchemasVersionsMatch.minor && isVersionValid

        if (hasAnyMismatch) {
            if (shouldShowNotification(guid, user.login)) {
                setShowVersionMismatch(true)
            } else {
                setShowVersionMismatch(false)
            }
        }

        if (versionsFullyCompatible) {
            removeDismissedTimestamp(guid, user.login)
            setShowVersionMismatch(false)
        }
    }, [schemaVersion, guid, user.login, dispatch, isSchemasVersionsMatch, isVersionValid, productConfig])

    if (!showVersionMismatch) return null

    return (
        <div
            className={`d-flex bg-white p-1 z-1 ${style.vMismatch}`}
        >
            <FontAwesomeIcon
                className="p-2"
                color={'#4B6687'}
                icon={faExclamationCircle}
            />
            <span className="p-1">
                <FormattedMessage
                    tagName="span"
                    id={'schema.versionNotMatchMessage'}
                    values={{
                        configurationSchemaVersion: String(schemaVersion),
                        actualSchemaVersion,
                    }}
                />{' '}
                &nbsp; &#x263a;
            </span>
            <button className="btn-clean p-1 pl-2 ml-auto mr-2 mb-auto">
                <FontAwesomeIcon
                    icon={faXmark}
                    onClick={handleClose}
                />
            </button>
        </div>
    )
}